import { createSelector } from 'reselect'

import { backendUrl } from 'utils/backendUrl'
import { checkPerm } from 'utils/permManager'
import { atLeastIronscalesProtectPlan, atLeastStarterPlan } from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'

import { PLAN_TYPES } from 'containers/App/constants'
import {
  selectActualBillableAgent,
  selectActualFeatureFlags,
  selectActualIntegrations,
  selectActualLicenses,
  selectBusinessPillar,
  selectIsStaticSalesDemo,
  selectPartner,
  selectRegion,
  selectSelfFeatureFlags,
  selectSelfLicenses,
  selectSelfPermissions,
} from 'containers/App/selectors'
import { selectHasPsimaRole } from 'containers/SpearPhishingAgent/accessSelectors'

const selectUserDropdownItems = (state) => {
  const selfPermissions = selectSelfPermissions(state)
  const actualLicenses = selectActualLicenses(state)
  const isStaticSalesDemo = selectIsStaticSalesDemo(state)
  const items = [
    {
      text: 'Resources',
      url: URL_CONST.resources.self,
      type: 'internal',
      id: 'resources_button',
      blocked: isStaticSalesDemo,
      className: 'gt-p',
    },
    {
      text: 'Log Out',
      // Signout is a Django full-page navigation; on static shells it must
      // target the backend origin.
      url: backendUrl(URL_CONST.login.signout),
      type: 'external',
      id: 'logout_button',
      className: 'red',
    },
  ]

  if (
    selfPermissions.admin &&
    actualLicenses?.plan &&
    actualLicenses?.plan !== PLAN_TYPES.STARTER
  ) {
    items.unshift({
      text: 'Activities',
      url: URL_CONST.activities.self,
      type: 'internal',
      id: 'activities_button',
      className: 'gt-p',
    })
  }
  return items
}

const selectIncidentsVisibility = createSelector(
  selectSelfPermissions,
  selectSelfLicenses,
  (selfPermissions, selfLicenses) =>
    checkPerm(selfPermissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
      'irontraps_viewer',
    ]) && atLeastStarterPlan(selfLicenses.plan)
)

const selectInvestigationVisibility = createSelector(
  selectSelfLicenses,
  selectSelfFeatureFlags,
  selectSelfPermissions,
  (selfLicenses, ff, selfPermissions) =>
    atLeastIronscalesProtectPlan(selfLicenses.plan) &&
    !ff.DISABLE_INVESTIGATION_TAB &&
    checkPerm(selfPermissions, [
      'admin',
      'irontraps_action',
      'manage_irontraps',
      'irontraps_viewer',
    ])
)

const selectAwarenessVisibility = createSelector(selectSelfPermissions, (selfPermissions) =>
  checkPerm(selfPermissions, ['admin', 'edit_campaigns'])
)

const selectMailboxesVisibility = createSelector(selectSelfPermissions, (selfPermissions) =>
  checkPerm(selfPermissions, ['admin'])
)

const selectGroupSettingsVisibility = createSelector(
  selectSelfPermissions,
  selectPartner,
  selectIsStaticSalesDemo,
  (selfPermissions, partner, isStaticSalesDemo) =>
    checkPerm(selfPermissions, ['admin']) && partner && !isStaticSalesDemo
)

const selectAccountSetupVisibility = createSelector(selectSelfPermissions, (selfPermissions) =>
  checkPerm(selfPermissions, ['admin'])
)

const selectDmarcVisibility = createSelector(
  selectRegion,
  selectSelfPermissions,
  (region, selfPermissions) => checkPerm(selfPermissions, ['admin']) && region !== 'CA'
)

const selectEncryptionPLGVisibility = createSelector(
  selectRegion,
  selectSelfPermissions,
  (region, selfPermissions) => checkPerm(selfPermissions, ['admin']) && region !== 'CA'
)

const selectDeepfakeVisibility = createSelector(selectSelfFeatureFlags, (ff) => ff.DEEPFAKE_SERVICE)

const selectRedTeamVisibility = createSelector(
  selectActualFeatureFlags,
  selectActualBillableAgent,
  // Same MSP-child default-enabled semantics as selectSpearPhishingAgentVisibility below.
  (ff, billableAgent) => !!(ff.ENABLE_RED_TEAM && (billableAgent?.redAgent ?? true))
)

const selectShouldShowPLGDF = createSelector(
  selectBusinessPillar,
  selectActualIntegrations,
  selectActualFeatureFlags,
  (businessPillar, integrations, ff) =>
    businessPillar !== 'MSP' && integrations?.o365 === true && !ff.DEEPFAKE_SERVICE
)

const selectSpearPhishingAgentVisibility = createSelector(
  selectPartner,
  selectActualFeatureFlags,
  selectActualBillableAgent,
  selectHasPsimaRole,
  (partner, ff, billableAgent, hasPsimaRole) =>
    hasPsimaRole &&
    // The selected tenant's own answer -- a missing billableAgent means enabled, as the
    // backend reads an absent row -- or, for a partner sitting on their own company, which
    // carries neither licence nor billed agent, whether any client has PSIMA at all.
    (!!(ff.PSIMA_SERVICE && (billableAgent?.psimaAgent ?? true)) || !!partner?.has_psima_companies)
)

export {
  selectUserDropdownItems,
  selectIncidentsVisibility,
  selectInvestigationVisibility,
  selectAwarenessVisibility,
  selectMailboxesVisibility,
  selectGroupSettingsVisibility,
  selectAccountSetupVisibility,
  selectDmarcVisibility,
  selectEncryptionPLGVisibility,
  selectDeepfakeVisibility,
  selectShouldShowPLGDF,
  selectSpearPhishingAgentVisibility,
  selectRedTeamVisibility,
}
